<template>
  <div class="v2026-limit" v-if="professionalId">
    <div class="v2026-limit__section-head">
      <span class="v2026-limit__section-label">{{ label }}</span>
      <template v-if="!isEditing">
        <button v-if="text" type="button" class="v2026-limit__icon-btn"
                @click="startEdit" aria-label="แก้ไขข้อจำกัด">
          <span class="v2026-limit__mask v2026-limit__edit" aria-hidden="true"
                :style="maskStyle(icons.edit)"></span>
        </button>
        <!-- Same 28px height as the icon button, so the head row never jumps. -->
        <button v-else type="button" class="v2026-limit__add"
                @click="startEdit" aria-label="เพิ่มข้อจำกัด">
          <span class="v2026-limit__add-ico" aria-hidden="true"
                :style="maskStyle(icons.plus)"></span>
          <span class="v2026-limit__add-text">เพิ่ม</span>
        </button>
      </template>
    </div>

    <div v-if="isEditing">
      <textarea class="v2026-limit__ta" rows="3" v-model="draft"
                placeholder="พิมพ์ข้อจำกัด…"></textarea>
      <div class="v2026-limit__editrow">
        <button type="button" class="v2026-limit__cancel"
                @click="isEditing = false">ยกเลิก</button>
        <button type="button" class="v2026-limit__save"
                :disabled="saving" @click="save">
          {{ saving ? 'กำลังบันทึก…' : 'บันทึก' }}
        </button>
      </div>
    </div>

    <template v-else>
      <div v-if="text" class="v2026-limit__box">{{ text }}</div>
      <div v-if="auditLine" class="v2026-limit__audit">
        แก้ไขล่าสุด: {{ auditLine.limitation_updated_at | prettyDate }} · {{ auditLine.name }}
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'limitation-section-v2026',
  props: {
    // null/0 → the whole section, affordance included, is not rendered.
    professionalId: {
      type: [Number, String],
      default: null
    },
    value: {
      type: String,
      default: null
    },
    audit: {
      type: Object,
      default: null
    },
    adminId: {
      type: [Number, String],
      default: null
    },
    adminName: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'ข้อจำกัด'
    }
  },
  data () {
    return {
      // The PUT does not commit to Vuex, so the just-saved text is held here.
      override: undefined,
      auditOverride: null, // { limitation_updated_at, name } after a local save
      draft: '',
      isEditing: false,
      saving: false, // PUT in flight → disable save
      icons: {
        edit: require('@/assets/icons2026/edit.svg'),
        plus: require('@/assets/icons2026/plus.svg')
      }
    }
  },
  computed: {
    text () {
      if (this.override !== undefined) return this.override
      return this.value
    },
    auditLine () {
      if (this.auditOverride) return this.auditOverride
      if (this.audit && this.audit.name) return this.audit
      return null
    }
  },
  watch: {
    // Reset on a target change, or a new chat shows the previous maid's just-saved text.
    professionalId (id, oldId) {
      if (id !== oldId) {
        this.override = undefined
        this.auditOverride = null
        this.isEditing = false
      }
    }
  },
  methods: {
    startEdit () {
      this.draft = this.text || ''
      this.isEditing = true
    },
    // FROZEN payload: PUT /beneat/professionals/{id} { limitation, limitation_update_by }.
    // $swal on failure is the existing maid-panel behaviour — do not "improve" it into a toast.
    async save () {
      if (!this.professionalId) return
      this.saving = true
      try {
        const { data } = await axios.put('/beneat/professionals/' + this.professionalId, {
          limitation: this.draft,
          limitation_update_by: this.adminId
        })
        const updatedAt = (data && data.professional && data.professional.limitation_updated_at)
          ? data.professional.limitation_updated_at : moment().format()
        this.override = this.draft
        this.auditOverride = {
          limitation_updated_at: updatedAt,
          name: this.adminName
        }
        this.isEditing = false
        // Only ever OVERWRITE existing keys — a new key would not be reactive in Vue 2.
        this.$emit('saved', { value: this.draft, audit: this.auditOverride })
      } catch (err) {
        this.$swal({
          text: 'ผิดพลาด ' + err.message,
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-limit {
  --v2026-limit-main: #31C1D7;
  --v2026-limit-surface-sunken: #F8FAFC;
  --v2026-limit-surface-hover: #e9ecf1;
  --v2026-limit-border: #e4e7ec;
  --v2026-limit-text: #101828;
  --v2026-limit-text-secondary: #667085;
  --v2026-limit-text-muted: #98a2b3;
  --v2026-limit-icon: #475467;

  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
}

.v2026-limit__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.v2026-limit__section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--v2026-limit-text-secondary);
}

.v2026-limit__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-limit__icon-btn:hover {
  background: var(--v2026-limit-surface-hover);
}
.v2026-limit__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-limit__mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-limit-icon);
}
.v2026-limit__edit {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-limit-icon);
}

.v2026-limit__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 6px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  color: var(--v2026-limit-main);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.v2026-limit__add:hover {
  background: rgba(49, 193, 215, 0.12);
}
.v2026-limit__add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-limit__add-ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-limit__ta {
  width: 100%;
  border: 1px solid var(--v2026-limit-border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: var(--v2026-limit-text);
  background: #fff;
  resize: vertical;
}
.v2026-limit__ta:focus {
  outline: none;
  border-color: var(--v2026-limit-main);
}
.v2026-limit__editrow {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.v2026-limit__cancel {
  border: 0;
  background: none;
  color: var(--v2026-limit-text-secondary);
  font-size: 12px;
  cursor: pointer;
}
.v2026-limit__save {
  border: 0;
  border-radius: 6px;
  padding: 8px 16px;
  background: var(--v2026-limit-main);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.v2026-limit__save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.v2026-limit__box {
  background: var(--v2026-limit-surface-sunken);
  border-radius: 14px;
  padding: 12px 16px;
  font-size: 14px;
  color: var(--v2026-limit-text);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.v2026-limit__audit {
  text-align: right;
  font-size: 12px;
  color: var(--v2026-limit-text-muted);
}
</style>
